import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import { useFloatingToolbarHost } from './FloatingToolbarHost';

// Shared geometry for canvas-anchored floating toolbars. Mirrors the values
// proven in TextFloatingToolbar so every element toolbar sits in the same
// place relative to the canvas.
export const FLOATING_TOOLBAR_HEIGHT = 44;
// Positive value pushes the toolbar lower (toward the canvas) so it doesn't
// hover tight against the canvas's top edge.
export const FLOATING_TOOLBAR_Y_OFFSET = 40;

export interface FloatingToolbarPosition {
  x: number;
  y: number;
}

/**
 * Computes a canvas-relative position for a floating toolbar and keeps it
 * fresh on canvas resize / window resize. Extracted from TextFloatingToolbar
 * so image/video/other element toolbars share one positioning implementation.
 *
 * @param active  whether the toolbar is currently shown (when false, position
 *                is cleared and observers are torn down)
 * @param elementId  the active element's id — recompute when it changes
 */
export function useFloatingToolbarPosition(
  active: boolean,
  elementId: string | undefined,
) {
  // The host's canvas (PLAT-12101): whichever renderer the bar is mounted over.
  const canvasElement = useFloatingToolbarHost()?.canvasElement;
  const toolbarRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<FloatingToolbarPosition | null>(null);

  const computePos = useCallback(() => {
    const canvasEl = canvasElement;
    if (!canvasEl) return;
    const cb = canvasEl.getBoundingClientRect();
    const tw = toolbarRef.current?.offsetWidth ?? 0;
    const x = Math.max(8, cb.left + (cb.width - tw) / 2);
    // Try to sit just above the canvas; fall back to just below the top edge.
    // Both anchors shift down by the offset so the toolbar isn't flush.
    const yAbove =
      cb.top - FLOATING_TOOLBAR_HEIGHT - 8 + FLOATING_TOOLBAR_Y_OFFSET;
    const y = yAbove >= 8 ? yAbove : cb.top + 8 + FLOATING_TOOLBAR_Y_OFFSET;
    setPos({ x, y });
  }, [canvasElement]);

  useLayoutEffect(() => {
    if (!active) {
      setPos(null);
      return;
    }
    computePos();
  }, [active, elementId, computePos]);

  useEffect(() => {
    if (!active) return;
    const ro = new ResizeObserver(computePos);
    if (canvasElement) ro.observe(canvasElement);
    window.addEventListener('resize', computePos);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', computePos);
    };
  }, [active, canvasElement, computePos]);

  return { toolbarRef, pos };
}
